// apps/web/src/pages/BillingProductsPage.tsx
//
// CRUD for billing_products (invoice templates / "productos"). Admin+ only.
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Package, AlertTriangle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePermissions } from '@/hooks/usePermissions';
import {
  listBillingProducts, createBillingProduct, updateBillingProduct, deleteBillingProduct,
  renderDescription, type BillingProduct, type BillingProductInput,
} from '@/services/billing';

const EMPTY_INPUT: BillingProductInput = {
  code: '',
  name: '',
  description: '',
  default_quantity: 1,
  default_unit_price: 0,
  currency: 'GTQ',
  is_service: true,
  unit_measure: 'UNI',
  is_active: true,
  sort_order: 100,
  patrol_monthly_free: null,
  max_sub_accounts: null,
};

export function BillingProductsPage() {
  const { can } = usePermissions();
  const canManage = can('billing.manage');

  const [products, setProducts] = useState<BillingProduct[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [editing, setEditing]   = useState<BillingProduct | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [draft, setDraft]       = useState<BillingProductInput>(EMPTY_INPUT);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { void refresh(); }, []);

  async function refresh() {
    setLoading(true);
    try {
      setProducts(await listBillingProducts());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los productos');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setDraft(EMPTY_INPUT);
    setCreating(true);
  }

  function openEdit(p: BillingProduct) {
    setEditing(p);
    setDraft({
      code: p.code, name: p.name, description: p.description,
      default_quantity: p.default_quantity, default_unit_price: p.default_unit_price,
      currency: p.currency, is_service: p.is_service, unit_measure: p.unit_measure,
      is_active: p.is_active, sort_order: p.sort_order,
      patrol_monthly_free: p.patrol_monthly_free,
      max_sub_accounts: p.max_sub_accounts,
    });
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (editing) await updateBillingProduct(editing.id, draft);
      else await createBillingProduct(draft);
      setEditing(null);
      setCreating(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      await deleteBillingProduct(deleteId);
      setDeleteId(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar');
    }
  }

  const preview = useMemo(
    () => renderDescription(draft.description || '', {
      siteName: 'Sitio Ejemplo',
      accountName: 'Cliente Ejemplo',
      nit: '1234567-8',
    }),
    [draft.description],
  );

  if (!canManage) {
    return (
      <div className="container mx-auto p-6">
        <Card><CardContent className="p-6 text-muted-foreground">
          Solo administradores pueden gestionar productos.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-6xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Package className="w-6 h-6" /> Productos de facturación
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Plantillas reutilizables que se usan al facturar (manual, batch o automático).
            La descripción puede usar variables: <code>{'{period}'}</code>, <code>{'{month}'}</code>,
            <code>{'{year}'}</code>, <code>{'{site_name}'}</code>, <code>{'{account_name}'}</code>,
            <code>{'{nit}'}</code>.
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Nuevo producto</Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/30 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Catálogo ({products.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Aún no hay productos. Creá el primero.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium">Código</th>
                  <th className="px-4 py-2 font-medium">Nombre</th>
                  <th className="px-4 py-2 font-medium">Descripción</th>
                  <th className="px-4 py-2 font-medium text-right">Precio</th>
                  <th className="px-4 py-2 font-medium text-center">Activo</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-t">
                    <td className="px-4 py-2 font-mono text-xs">{p.code}</td>
                    <td className="px-4 py-2">{p.name}</td>
                    <td className="px-4 py-2 max-w-md truncate text-muted-foreground text-xs" title={p.description}>{p.description}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.currency} {p.default_unit_price.toFixed(2)}</td>
                    <td className="px-4 py-2 text-center">
                      {p.is_active ? <Badge variant="secondary">Sí</Badge> : <Badge variant="outline">No</Badge>}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteId(p.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); }}}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
            <DialogDescription>
              Usá variables en la descripción para personalizar cada factura emitida.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Código</Label>
              <Input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value.toUpperCase() })} placeholder="MON-COM" />
            </div>
            <div>
              <Label>Nombre interno</Label>
              <Input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Monitoreo Comercial" />
            </div>
            <div className="col-span-2">
              <Label>Descripción (para SAT)</Label>
              <textarea
                className="w-full min-h-32 px-3 py-2 text-sm rounded-md border bg-background"
                value={draft.description}
                onChange={e => setDraft({ ...draft, description: e.target.value })}
                placeholder="Monitoreo al sistema de Alarma Comercial Pago correspondiente al mes de {month} {year}..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Vista previa: <span className="text-foreground">{preview || '—'}</span>
              </p>
            </div>
            <div>
              <Label>Cantidad por defecto</Label>
              <Input type="number" step="0.0001" value={draft.default_quantity}
                onChange={e => setDraft({ ...draft, default_quantity: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Precio unitario (con IVA)</Label>
              <Input type="number" step="0.01" value={draft.default_unit_price}
                onChange={e => setDraft({ ...draft, default_unit_price: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Unidad</Label>
              <Input value={draft.unit_measure}
                onChange={e => setDraft({ ...draft, unit_measure: e.target.value.toUpperCase() })} placeholder="UNI" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={draft.is_service ? 'service' : 'good'}
                onValueChange={v => setDraft({ ...draft, is_service: v === 'service' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Servicio</SelectItem>
                  <SelectItem value="good">Bien</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={draft.is_active} onCheckedChange={v => setDraft({ ...draft, is_active: v })} />
              <Label>Activo (visible al facturar)</Label>
            </div>
            <div className="col-span-2">
              <Label>Patrullas gratuitas / mes</Label>
              <Input
                type="number" min="0" step="1"
                placeholder="Sin límite (dejar vacío = cobrar siempre)"
                value={draft.patrol_monthly_free ?? ''}
                onChange={e => setDraft({ ...draft, patrol_monthly_free: e.target.value !== '' ? parseInt(e.target.value, 10) : null })}
              />
              <p className="text-xs text-muted-foreground mt-0.5">
                Vacío = se cobra cada despacho. 0 = se cobra desde el primero. N = primeros N despachos del mes son gratis.
              </p>
            </div>
            <div className="col-span-2">
              <Label>Subcuentas máximas</Label>
              <Input
                type="number" min="1" step="1"
                placeholder="Sin límite (dejar vacío)"
                value={draft.max_sub_accounts ?? ''}
                onChange={e => setDraft({ ...draft, max_sub_accounts: e.target.value !== '' ? parseInt(e.target.value, 10) : null })}
              />
              <p className="text-xs text-muted-foreground mt-0.5">
                Máximo de miembros activos por cuenta. Vacío = ilimitado.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !draft.code || !draft.name || !draft.description}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar producto?</DialogTitle>
            <DialogDescription>
              No se eliminará si está siendo usado por suscripciones activas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
